import { Injectable } from '@angular/core';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { HttpClient } from '@angular/common/http';
import { BaseDataService } from './base.data.service';
import { IInteractionChannel } from '../interfaces/interactions/interaction-channel';
import { IOrganisationInteraction } from '../interfaces/interactions/organisation-interaction';
import { IInteractionCategory } from '../interfaces/interactions/interaction-category';
import { IPageResult } from '../interfaces/page-result';
import { IInteractionFilter } from '../interfaces/interactions/interaction-filter';
import moment from 'moment';
import { IInteractionDepartment } from '../interfaces/interactions/interaction-department';

@Injectable({ providedIn: 'root' })
export class OrganisationInteractionService extends BaseDataService {
  protected entityTitle = 'Organisation Interaction';
  protected url = 'api/organisation-interactions';

  constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
    super(http, handlersFactory);
  }

  pinInteraction(interactionId: number): Promise<IOrganisationInteraction> {
    const url = `${this.url}/${interactionId}/pin`;
    return this.handleRequest(
      this.http.get<IOrganisationInteraction>(`${url}`),
      this.handlersFactory.getDefault(),
    );
  }

  getInteractionDepartments(): Promise<IInteractionDepartment[]> {
    return this.handleRequest(
      this.http.get<IInteractionDepartment[]>(`${this.url}/departments`),
      this.handlersFactory.getDefault(),
    );
  }

  getInteractionChannels(): Promise<IInteractionChannel[]> {
    return this.handleRequest(
      this.http.get<IInteractionChannel[]>(`${this.url}/channels`),
      this.handlersFactory.getDefault(),
    );
  }

  getInteractionCategories(activeOnly = false): Promise<IInteractionChannel[]> {
    const url = `${this.url}/categories/${activeOnly ? 'active' : ''}`;
    return this.handleRequest(
      this.http.get<IInteractionChannel[]>(`${url}`),
      this.handlersFactory.getDefault(),
    );
  }

  addInteraction(interaction: IOrganisationInteraction): Promise<IOrganisationInteraction> {
    const url = `${this.url}`;
    return this.handleRequest<IOrganisationInteraction>(
      this.http.post<IOrganisationInteraction>(url, interaction),
      this.handlersFactory.getForCreate(this.entityTitle),
    );
  }

  updateInteraction(interaction: IOrganisationInteraction): Promise<IOrganisationInteraction> {
    const url = `${this.url}`;
    return this.handleRequest<IOrganisationInteraction>(
      this.http.put<IOrganisationInteraction>(url, interaction),
      this.handlersFactory.getForUpdate(this.entityTitle),
    );
  }

  addInteractionCategory(category: IInteractionCategory): Promise<IInteractionCategory> {
    const url = `${this.url}/categories`;
    return this.handleRequest<IInteractionCategory>(
      this.http.post<IInteractionCategory>(url, category),
      this.handlersFactory.getForCreate(this.entityTitle),
    );
  }

  updateInteractionCategory(id: number, patch: any): Promise<IInteractionCategory> {
    const url = `${this.url}/category/${id}`;
    return this.handleRequest<IInteractionCategory>(
      this.http.patch<IInteractionCategory>(url, patch),
      this.handlersFactory.getForCreate(this.entityTitle),
    );
  }

  setCategoryActivation(categoryId: number): Promise<IInteractionCategory> {
    const url = `${this.url}/categories/activation/${categoryId}`;
    return this.handleRequest(
      this.http.get<IInteractionCategory>(`${url}`),
      this.handlersFactory.getDefault(),
    );
  }

  getInteractions(organisationId: number): Promise<IOrganisationInteraction[]> {
    const url = `${this.url}/${organisationId}`;
    return this.handleRequest(
      this.http.get<IOrganisationInteraction[]>(`${url}`),
      this.handlersFactory.getDefault(),
    );
  }

  getLatestInteraction(organisationId: number): Promise<IOrganisationInteraction> {
    const url = `${this.url}/${organisationId}`;
    return this.handleRequest(
      this.http.get<IOrganisationInteraction>(`${url}/latest`),
      this.handlersFactory.getDefault(),
    );
  }

  getInteractionsReport(
    filterCommand: IInteractionFilter,
  ): Promise<IPageResult<IOrganisationInteraction>> {
    return this.handleRequest<IPageResult<IOrganisationInteraction>>(
      this.http.get<IPageResult<IOrganisationInteraction>>(`${this.url}/report`, {
        params: this.constructHttpParams(filterCommand),
      }),
      this.handlersFactory.getForReadByFilter(),
    );
  }

  getWarningLevel(interactionDate: Date) {
    const wd = moment().diff(moment(interactionDate), 'weeks');
    if (wd >= 4) {
      return 2;
    } else if (wd >= 2) {
      return 1;
    }

    return 0;
  }
}
